// Small date/label formatters shared across the Planning / Procurement /
// TenderOpening / InvitationExternal modules.
//
// ⚠️ All three display formatters render in Jakarta time, NOT the viewer's zone.
//
// Every `DateTimeOffset` returned by a GET in these contracts carries an
// explicit `+00:00` offset and is a genuine UTC instant, so it has to be shifted
// by +7 to read as the Jakarta wall-clock users expect. Rendering in the
// browser's own zone was rejected: these are Pertamina-internal screens that
// must show the same time on every machine, which is also what the TenderOpening
// contract spells out ("render in Jakarta (GMT+07:00) by default regardless of
// the viewer's machine timezone").
//
// `JAKARTA_UTC_OFFSET_HOURS` below is the same decision expressed for the Excel
// exports, whose `utcOffsetHours` param has to be handed the number rather than
// a zone name — the two must move together or the file and the screen disagree.
//
// `toIsoDate` stays local: it reads locally-picked `Date` objects, not API
// values.

const DISPLAY_TIME_ZONE = 'Asia/Jakarta';

// Jakarta's fixed offset, for the Excel exports' `utcOffsetHours` param
// (api-contract-planning.md section 3). Indonesia has no DST, so a constant is
// exact — there is no drift against DISPLAY_TIME_ZONE above.
export const JAKARTA_UTC_OFFSET_HOURS = 7;

// "05 Jan 2026, 08:30" — used in list columns and export rows.
export const formatDateTime = value => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: DISPLAY_TIME_ZONE,
  });
};

// "16 Juli 2026 10.15 WIB" — jadwal (Submission Deadline / Validity Period)
// pada list Procurement, mengikuti format desain acuan (bulan Indonesia,
// pemisah jam titik, sufiks WIB).
export const formatDateTimeWib = value => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const datePart = d.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: DISPLAY_TIME_ZONE,
  });
  const timePart = d.toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: DISPLAY_TIME_ZONE,
  });
  return `${datePart} ${timePart.replace(':', '.')} WIB`;
};

// "05 Jan 2026" — date only.
export const formatDate = value => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: DISPLAY_TIME_ZONE,
  });
};

// "Aug 2026" — month picker display for contract periods.
export const formatMonth = value => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric',
    timeZone: DISPLAY_TIME_ZONE,
  });
};

// The viewer's own UTC offset in whole hours.
//
// NOT used by default — the screens and the Excel exports are both pinned to
// Jakarta (see above), so this is the ready-made swap for the day per-user
// timezones are wanted: pass it as `utcOffsetHours` and switch the formatters'
// `DISPLAY_TIME_ZONE` to `undefined` (which makes `toLocaleString` use the
// viewer's zone) so the file and the screen stay in step. Changing only one of
// the two puts them back out of sync by the local offset.
//
// Read at call time, not module load, so a DST change mid-session is picked up.
// `getTimezoneOffset()` counts minutes WEST of UTC, so WIB (UTC+7) reports -420
// and the sign has to be flipped. The API takes an int in -12..14 and 400s
// outside it, hence the round + clamp: a half-hour zone (India UTC+5:30, Nepal
// +5:45) cannot be expressed and lands on the nearer hour.
export const localUtcOffsetHours = () => {
  const minutesWestOfUtc = new Date().getTimezoneOffset();
  if (Number.isNaN(minutesWestOfUtc)) return 7;
  // `|| 0` normalises the -0 that UTC (0 minutes) would otherwise produce.
  const hours = Math.round(-minutesWestOfUtc / 60) || 0;
  return Math.min(14, Math.max(-12, hours));
};

// Convert a Date (or ISO string) to yyyy-mm-dd for storing DDR / period values.
export const toIsoDate = value => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
